import { Flex, Stack, Text } from '@chakra-ui/react';

import type { Quake } from '../api/types';

// ScoreBreakdown renders the evidence components behind Quake.Score.
// Move, volume, and capital form raw evidence; spread/depth feed
// liquidity quality and reliability.
//
// Visible in the table, not just a title tooltip, so the score is
// explainable on mobile and to anyone who didn't hover.

export function scoreBreakdown(mover: Quake) {
  return {
    spike: mover.score_spike,
    capital: mover.score_capital,
    move: mover.score_move,
    spread: mover.score_spread,
    depth: mover.score_depth,
  };
}

export function ScoreBreakdownCells({ mover }: { mover: Quake }) {
  const b = scoreBreakdown(mover);
  return (
    <Stack
      gap="1"
      title={`Spike ${(b.spike * 100).toFixed(0)}%  Capital ${(b.capital * 100).toFixed(0)}%  Move ${(b.move * 100).toFixed(0)}%  Spread ${(b.spread * 100).toFixed(0)}%  Depth ${(b.depth * 100).toFixed(0)}%`}
    >
      <ScoreBars label="vol" value={b.spike} />
      <ScoreBars label="cap" value={b.capital} />
      <ScoreBars label="mov" value={b.move} />
    </Stack>
  );
}

// ScoreBars renders one component's contribution as a small bar. We
// show three at once (vol / cap / mov) since they are the raw evidence
// ingredients. Spread and Depth are shown in the native title only.
function ScoreBars({ label, value }: { label: string; value: number }) {
  const pct = Math.max(0, Math.min(1, value));
  const widthPct = Math.round(pct * 100);
  // 8-step bar, no decimals. Renders the same shape on every row.
  const dots = '▁▂▃▄▅▆▇█';
  const filled = Math.max(0, Math.min(8, Math.round(pct * 8)));
  const bar = dots.slice(0, filled) + '·'.repeat(Math.max(0, 8 - filled));
  return (
    <Flex align="center" gap="1" fontSize="10px" lineHeight="1" color="text.muted">
      <Text w="6" textTransform="uppercase">
        {label}
      </Text>
      <Text fontFamily="mono" letterSpacing="tight" color="text.primary">
        {bar}
      </Text>
      <Text w="7" textAlign="right" fontVariantNumeric="tabular-nums" color="text.muted">
        {widthPct}%
      </Text>
    </Flex>
  );
}